import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { FormWrapper } from "@/components/Forms/components/MonolithFormComponents";
import { useForm, zodResolver, UseFormReturnType } from "@mantine/form";
import {
  Button,
  DateInput,
  Grid,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { useRef, useState } from "react";
import z from "zod";
import { ChainOfCustodyFormValues } from "../coc-api.types";
import { SignatureField } from "@/Features/Signatures";
import { LocationSelector } from "@/Features/Locations";

type FormOption = {
  visible: boolean;
  disabled: boolean;
  label: string;
  description: string;
  type: "text" | "location" | "signature";
  required: boolean;
};

const BaseFormOptions: Record<
  keyof Omit<ChainOfCustodyFormValues, "type">,
  FormOption
> = {
  timestamp: {
    visible: true,
    disabled: false,
    label: "Timestamp",
    type: "text",
    required: true,
    description: "Date and time of the chain of custody event",
  },
  custody_to: {
    visible: true,
    disabled: false,
    label: "Custody To",
    type: "text",
    required: true,
    description: "Person or location receiving item",
  },
  custody_from: {
    visible: true,
    disabled: false,
    label: "Custody From",
    type: "text",
    required: true,
    description:
      "Person, location, or method from which the item was received from",
  },
  custody_to_sig: {
    visible: true,
    disabled: false,
    label: "Custody To Signature",
    type: "signature",
    required: false,
    description: "Signature of the person or location receiving item",
  },
  custody_from_sig: {
    visible: true,
    disabled: false,
    label: "Custody From Signature",
    type: "signature",
    required: false,
    description: "Signature of the person or location receiving item",
  },
  location_received: {
    visible: true,
    disabled: false,
    label: "Location Received",
    type: "text",
    required: false,
    description:
      "The address or general location where the custody event occurred",
  },
  reason: {
    visible: true,
    disabled: false,
    label: "Notes",
    type: "text",
    required: false,
    description: "Any additional notes for this chain of custody event",
  },
};

const FormOptions: Record<
  ChainOfCustodyFormValues["type"],
  Record<keyof typeof BaseFormOptions, FormOption>
> = {
  Intake: {
    ...BaseFormOptions,
    custody_to: {
      ...BaseFormOptions.custody_to,
      label: "Received By (Custody To)",
      description: "This is the person or location receiving the item",
      required: true,
      type: "location",
    },
    custody_to_sig: {
      ...BaseFormOptions.custody_to_sig,
      label: "Received By Signature",
      description:
        "This is the signature of the person or location receiving the item",
      required: false,
      type: "signature",
    },
    custody_from_sig: {
      ...BaseFormOptions.custody_from_sig,
      label: "Received From Signature",
      description:
        "This is the signature of the person or location receiving the item",
      required: false,
      type: "signature",
    },
    custody_from: {
      ...BaseFormOptions.custody_from,
      label: "Received From (Custody From)",
      description:
        "Person, location, or method from which the item was received",
      required: true,
      type: "text",
    },
    timestamp: {
      ...BaseFormOptions.timestamp,
      description: "Date and time the item was received",
      required: true,
      type: "text",
    },
  },
  Move: {
    ...BaseFormOptions,
    custody_to: {
      ...BaseFormOptions.custody_to,
      label: "Move To (Custody To)",
      description: "This is the person or location the item will be moved to",
      required: true,
      type: "location",
    },
    custody_to_sig: {
      ...BaseFormOptions.custody_to_sig,
      label: "Move To Signature",
      description:
        "This is the signature of the person or location the item will be moved to",
      required: false,
      type: "signature",
    },
    custody_from: {
      ...BaseFormOptions.custody_from,
      label: "Move From (Custody From)",
      description:
        "The item will be moved from its previous location. This field is disabled and is defaulted to the previous location",
      disabled: true,
      required: true,
      type: "location",
    },
    custody_from_sig: {
      ...BaseFormOptions.custody_from_sig,
      disabled: true,
      label: "Move From Signature",
      description:
        "This is the signature of the person or location the item will be moved from. This field is disabled.",
      required: false,
      type: "signature",
    },
    location_received: {
      ...BaseFormOptions.location_received,
      required: false,
      visible: false,
    },
  },
  Checkout: {
    ...BaseFormOptions,
    custody_to: {
      ...BaseFormOptions.custody_to,
      label: "Checkout To (Custody To)",
      description:
        "This is the person or location the item will be checked out to",
      required: true,
      disabled: true,
      type: "location",
    },
    custody_to_sig: {
      ...BaseFormOptions.custody_to_sig,
      label: "Checkout To Signature",
      description:
        "This is the signature of the person or location the item will be checked out to",
      required: false,
      type: "signature",
    },
    custody_from: {
      ...BaseFormOptions.custody_from,
      label: "Checkout From (Custody From)",
      description:
        "The item will be checked out from its previous location. This field is disabled and is defaulted to the previous location",
      required: true,
      disabled: true,
      type: "location",
    },
    custody_from_sig: {
      ...BaseFormOptions.custody_from_sig,
      disabled: true,
      label: "Checkout From Signature",
      description:
        "This is the signature of the person or location the item will be checked out from. This field is disabled.",
      required: false,
      type: "signature",
    },
    location_received: {
      ...BaseFormOptions.location_received,
      required: false,
      visible: false,
    },
  },
  Release: {
    ...BaseFormOptions,
    custody_to: {
      ...BaseFormOptions.custody_to,
      label: "Release To (Custody To)",
      description:
        "This is the person or location the item will be released to",
      required: true,
      type: "text",
    },
    custody_to_sig: {
      ...BaseFormOptions.custody_to_sig,
      label: "Release To Signature",
      description:
        "This is the signature of the person or location the item will be released to",
      required: false,
      type: "signature",
    },
    custody_from: {
      ...BaseFormOptions.custody_from,
      label: "Release From (Custody From)",
      description: "The item will be released from its previous location.",
      required: true,
      type: "location",
    },
    custody_from_sig: {
      ...BaseFormOptions.custody_from_sig,
      label: "Release From Signature",
      description:
        "This is the signature of the person or location the item will be released from.",
      required: false,
      type: "signature",
    },
    location_received: {
      ...BaseFormOptions.location_received,
      required: false,
      visible: false,
    },
  },
  "Re-Collect": {
    ...BaseFormOptions,
    custody_to: {
      ...BaseFormOptions.custody_to,
      required: true,
      type: "location",
    },
  },
  Destroy: {
    ...BaseFormOptions,
    custody_to: {
      ...BaseFormOptions.custody_to,
      label: "To Destroyed (Custody To)",
      description:
        "The item will be destroyed. This field is disabled and is defaulted to the destroyed location",
      disabled: true,
      required: true,
      type: "location",
    },
    custody_to_sig: {
      ...BaseFormOptions.custody_to_sig,
      label: "Destroyed Signature",
      description: "The signature of the person destroying the item.",
      disabled: true,
      required: false,
      type: "signature",
    },
    custody_from: {
      ...BaseFormOptions.custody_from,
      label: "Destroyed By (Custody From)",
      description: "The item will be destroyed by this person.",
      required: true,
      type: "location",
    },
    custody_from_sig: {
      ...BaseFormOptions.custody_from_sig,
      label: "Destroyed By Signature",
      description: "The signature of the person destroying the item.",
      required: false,
      type: "signature",
    },
    location_received: {
      ...BaseFormOptions.location_received,
      required: false,
      visible: false,
    },
  },
};

export type ChainOfCustodyFormProps = {
  submitting?: boolean;
  onCancel?: () => void;
  onSubmit?: (d: ChainOfCustodyFormValues) => void;
  onChange?: (values: ChainOfCustodyFormValues) => void;
  showButtonMenu?: boolean;
  defaultFormData?: ChainOfCustodyFormValues;
  disabled?: boolean;
  type: ChainOfCustodyFormValues["type"];
};

const OptionSchema = z.object({
  label: z.string(),
  value: z.union([z.string(), z.number()]),
});

const SignatureSchema = z.object({
  signature: z.string(),
  signer: z.string().optional(),
  timestamp: z.string(),
  userAgent: z.string(),
  isESignature: z.boolean().optional(),
  esig: z.boolean().optional(),
  ip_address: z.string().optional(),
});

export const ChainOfCustodyFormSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("Intake"),
      timestamp: z
        .string({ message: "Timestamp is required" })
        .min(1, { message: "Timestamp is required" }),
      custody_to: OptionSchema,
      custody_from: z.string().min(1),
      custody_to_id: z.coerce.number().min(1).optional(),
      custody_from_id: z.coerce.number().min(1).optional(),
      custody_from_sig: SignatureSchema.optional(),
      custody_to_sig: SignatureSchema.optional(),
      location_received: z.string().optional(),
      reason: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("Move"),
      timestamp: z
        .string({ message: "Timestamp is required" })
        .min(1, { message: "Timestamp is required" }),
      custody_to: OptionSchema,
      custody_from: OptionSchema,
      custody_to_id: z.coerce.number().min(1).optional(),
      custody_from_id: z.coerce.number().min(1).optional(),
      custody_from_sig: SignatureSchema.optional(),
      custody_to_sig: SignatureSchema.optional(),
      reason: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("Checkout"),
      timestamp: z
        .string({ message: "Timestamp is required" })
        .min(1, { message: "Timestamp is required" }),
      custody_to: OptionSchema,
      custody_from: OptionSchema,
      custody_to_id: z.coerce.number().min(1).optional(),
      custody_from_id: z.coerce.number().min(1).optional(),
      custody_from_sig: SignatureSchema.optional(),
      custody_to_sig: SignatureSchema.optional(),
      reason: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("Release"),
      timestamp: z
        .string({ message: "Timestamp is required" })
        .min(1, { message: "Timestamp is required" }),
      custody_to: z.string().min(1),
      custody_from: OptionSchema,
      custody_to_id: z.coerce.number().min(1).optional(),
      custody_from_id: z.coerce.number().min(1).optional(),
      custody_from_sig: SignatureSchema.optional(),
      custody_to_sig: SignatureSchema.optional(),
      reason: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("Re-Collect"),
      timestamp: z
        .string({ message: "Timestamp is required" })
        .min(1, { message: "Timestamp is required" }),
      custody_to: OptionSchema,
      custody_from: z.string().min(1),
      custody_to_id: z.coerce.number().min(1).optional(),
      custody_from_id: z.coerce.number().min(1).optional(),
      custody_from_sig: SignatureSchema.optional(),
      custody_to_sig: SignatureSchema.optional(),
      reason: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("Destroy"),
      timestamp: z
        .string({ message: "Timestamp is required" })
        .min(1, { message: "Timestamp is required" }),
      custody_to: OptionSchema,
      custody_from: OptionSchema,
      custody_to_id: z.coerce.number().min(1).optional(),
      custody_from_id: z.coerce.number().min(1).optional(),
      custody_from_sig: SignatureSchema.optional(),
      custody_to_sig: SignatureSchema.optional(),
      reason: z.string().optional(),
    })
    .strict(),
]);

const renderCustodyTo = (
  option: FormOption,
  form: UseFormReturnType<ChainOfCustodyFormValues>,
  disabled: boolean
) => {
  if (option.visible === false) {
    return null;
  }

  if (option.type === "location") {
    return (
      <LocationSelector
        label={option.label}
        description={option.description}
        placeholder="Select location"
        required={option.required}
        disabled={disabled || option.disabled}
        {...form.getInputProps("custody_to")}
        key={form.key("custody_to")}
        onChange={(_, option) => {
          form.setFieldValue("custody_to_id", option?.value);
          form.setFieldValue("custody_to", option);
        }}
      />
    );
  }

  return (
    <TextInput
      label={option.label}
      description={option.description}
      placeholder="Enter custody to..."
      required={option.required}
      disabled={disabled || option.disabled}
      {...form.getInputProps("custody_to")}
      key={form.key("custody_to")}
    />
  );
};

const renderCustodyFrom = (
  option: FormOption,
  form: UseFormReturnType<ChainOfCustodyFormValues>,
  disabled: boolean
) => {
  if (option.visible === false) {
    return null;
  }

  if (option.type === "location") {
    return (
      <LocationSelector
        label={option.label}
        description={option.description}
        placeholder="Select location"
        required={option.required}
        disabled={disabled || option.disabled}
        {...form.getInputProps("custody_from")}
        key={form.key("custody_from")}
        onChange={(_, option) => {
          form.setFieldValue("custody_from_id", option?.value);
          form.setFieldValue("custody_from", option);
        }}
      />
    );
  }

  return (
    <TextInput
      label={option.label}
      description={option.description}
      placeholder="Enter custody from..."
      required={option.required}
      disabled={disabled || option.disabled}
      {...form.getInputProps("custody_from")}
      key={form.key("custody_from")}
    />
  );
};

const renderCustodyToSignature = (
  option: FormOption,
  form: UseFormReturnType<ChainOfCustodyFormValues>,
  disabled: boolean
) => {
  if (option.visible === false) {
    return null;
  }

  return (
    <SignatureField
      description={option.description}
      useESignature={true}
      disabled={disabled || option.disabled}
      {...form.getInputProps("custody_to_sig")}
      key={form.key("custody_to_sig")}
    />
  );
};

const renderCustodyFromSignature = (
  option: FormOption,
  form: UseFormReturnType<ChainOfCustodyFormValues>,
  disabled: boolean
) => {
  if (option.visible === false) {
    return null;
  }

  return (
    <SignatureField
      description={option.description}
      useESignature={true}
      disabled={disabled || option.disabled}
      {...form.getInputProps("custody_from_sig")}
      key={form.key("custody_from_sig")}
    />
  );
};

const renderTimestamp = (
  option: FormOption,
  form: UseFormReturnType<ChainOfCustodyFormValues>,
  disabled: boolean
) => {
  if (option.visible === false) {
    return null;
  }

  return (
    <div style={{ gridColumn: "1 / -1" }}>
      <DateInput
        size="sm"
        variant="outlined"
        label={option.label}
        description={option.description}
        enableCalendar
        arrow
        clearable={true}
        disabled={disabled || option.disabled}
        format="YYYY-MM-DD HH:mm:ss"
        required={option.required}
        key={form.key("timestamp")}
        {...form.getInputProps("timestamp")}
      />
    </div>
  );
};

const renderLocationReceived = (
  option: FormOption,
  form: UseFormReturnType<ChainOfCustodyFormValues>,
  disabled: boolean
) => {
  if (option.visible === false) {
    return null;
  }

  return (
    <div style={{ gridColumn: "1 / -1" }}>
      <TextInput
        size="sm"
        variant="outlined"
        label={option.label}
        placeholder="Address, Front Office, etc..."
        disabled={disabled || option.disabled}
        description={option.description}
        required={option.required}
        key={form.key("location_received")}
        {...form.getInputProps("location_received")}
      />
    </div>
  );
};

const renderNotes = (
  option: FormOption,
  form: UseFormReturnType<ChainOfCustodyFormValues>,
  disabled: boolean
) => {
  if (option.visible === false) {
    return null;
  }

  return (
    <div style={{ gridColumn: "1 / -1" }}>
      <TextAreaInput
        size="sm"
        variant="outlined"
        label={option.label}
        placeholder={
          "Enter any additional notes for this chain of custody event"
        }
        description={option.description}
        minRows={4}
        disabled={disabled || option.disabled}
        required={option.required}
        {...form.getInputProps("reason")}
        key={form.key("reason")}
      />
    </div>
  );
};

export const ChainOfCustodyForm: React.FC<ChainOfCustodyFormProps> = ({
  submitting,
  onCancel,
  onSubmit,
  onChange,
  showButtonMenu = true,
  disabled = false,
  defaultFormData,
  type,
}) => {
  const [submittingState, setSubmittingState] = useState(false);

  const isSubmitControlled = useRef(submitting !== undefined);

  const _submitting = isSubmitControlled.current ? submitting : submittingState;

  const form = useForm<ChainOfCustodyFormValues>({
    initialValues: {
      type,
      ...defaultFormData,
    } as ChainOfCustodyFormValues,
    validate: zodResolver(ChainOfCustodyFormSchema),
    onValuesChange: (values) => {
      values.type = type;
      onChange?.(values);
    },
  });

  const formData = form.getValues();

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const handleSubmit = () => {
    setSubmittingState(true);
    form.setFieldValue("type", type);
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      setSubmittingState(false);
      return;
    }

    onSubmit?.(formData);
    setSubmittingState(false);
  };

  const formOptions = FormOptions[type] ?? {};

  const formDisabled = (disabled || _submitting) ?? false;

  return (
    <FormWrapper>
      <Grid col={1}>
        {/* Custody To */}
        {renderCustodyTo(
          formOptions.custody_to as FormOption,
          form,
          formDisabled
        )}
        {/* Custody To Signature */}
        {renderCustodyToSignature(
          formOptions.custody_to_sig as FormOption,
          form,
          formDisabled
        )}
        {/* Custody From */}
        {renderCustodyFrom(
          formOptions.custody_from as FormOption,
          form,
          formDisabled
        )}
        {/* Custody From Signature */}
        {renderCustodyFromSignature(
          formOptions.custody_from_sig as FormOption,
          form,
          formDisabled
        )}
        {/* Timestamp */}
        {renderTimestamp(
          formOptions.timestamp as FormOption,
          form,
          formDisabled
        )}
        {/* Location Received */}
        {renderLocationReceived(
          formOptions.location_received as FormOption,
          form,
          formDisabled
        )}
        {/* Notes */}
        {renderNotes(formOptions.reason as FormOption, form, formDisabled)}
      </Grid>
      {showButtonMenu && (
        <ButtonMenu>
          <Button
            size="xs"
            variant="subtle"
            onClick={handleCancel}
            disabled={_submitting || disabled}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={_submitting || disabled}
            loading={_submitting}
          >
            Submit
          </Button>
        </ButtonMenu>
      )}
    </FormWrapper>
  );
};
